import { useState, useEffect, useMemo } from "react";
import {
  MdWorkOutline, MdOutlinePeople, MdOutlineStarBorder,
  MdOutlineCalendarToday,
  MdOutlineCheckCircle, MdOutlineCancel,
} from "react-icons/md";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../../StyledCss/styledInputs";
import { getHiringOverview } from "../../../api/services/hroneApi";
import "./index.css";

/* Ranges are computed client-side into from/to dates and sent to the
   /v1/hiring/overview endpoint (from_date/to_date, both required). */
const RANGE_OPTIONS = [
  { id: "LAST_1M", label: "Last 1 Month",  months: 1 },
  { id: "LAST_3M", label: "Last 3 Months", months: 3 },
  { id: "LAST_6M", label: "Last 6 Months", months: 6 },
  { id: "LAST_1Y", label: "Last 1 Year",   months: 12 },
  { id: "CUSTOM",  label: "Custom Range",  months: null },
];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/**
 * Hiring Overview cards.
 *
 * Notifies the parent of the resolved from/to dates via onDateRangeChange so
 * the Job Openings table can query the exact same published_date window —
 * otherwise dashboard totals and the per-job columns can never tally.
 */
const HiringOverview = ({ refreshTick, onDateRangeChange }) => {
  const [range, setRange]           = useState("LAST_1M");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo]     = useState("");
  const [stats, setStats]           = useState({ jobsPosted: 0, applications: 0, shortlisted: 0, hired: 0, rejected: 0 });
  const [loading, setLoading]       = useState(true);

  /* Resolve the active range into concrete from/to ISO dates. */
  const { fromDate, toDate } = useMemo(() => {
    if (range === "CUSTOM") return { fromDate: customFrom, toDate: customTo };
    const opt = RANGE_OPTIONS.find((r) => r.id === range);
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - opt.months);
    return { fromDate: toISO(from), toDate: toISO(to) };
  }, [range, customFrom, customTo]);

  // Keep the job table on the same date window as these cards.
  useEffect(() => {
    if (typeof onDateRangeChange !== "function") return;
    if (range === "CUSTOM" && (!fromDate || !toDate)) {
      onDateRangeChange({ fromDate: null, toDate: null });
      return;
    }
    onDateRangeChange({ fromDate, toDate });
  }, [range, fromDate, toDate, onDateRangeChange]);

  useEffect(() => {
    // Custom range needs both ends before we can query.
    if (range === "CUSTOM" && (!fromDate || !toDate)) {
      setStats({ jobsPosted: 0, applications: 0, shortlisted: 0, hired: 0, rejected: 0 });
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res  = await getHiringOverview({ from_date: fromDate, to_date: toDate });
        const data = await res.json();
        if (cancelled) return;

        if (data.status) {
          setStats({
            jobsPosted:   data.data.jobs_posted   || 0,
            applications: data.data.applications  || 0,
            shortlisted:  data.data.shortlisted   || 0,
            hired:        data.data.hired         || 0,
            rejected:     data.data.rejected      || 0,
          });
        } else {
          setStats({ jobsPosted: 0, applications: 0, shortlisted: 0, hired: 0, rejected: 0 });
        }
      } catch {
        if (!cancelled) setStats({ jobsPosted: 0, applications: 0, shortlisted: 0, hired: 0, rejected: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [range, fromDate, toDate, refreshTick]);

  const cards = [
    { key: "jobsPosted",   label: "Jobs Posted",     value: stats.jobsPosted,   icon: <MdWorkOutline /> },
    { key: "applications", label: "Applications",    value: stats.applications, icon: <MdOutlinePeople /> },
    { key: "shortlisted",  label: "Shortlisted",     value: stats.shortlisted,  icon: <MdOutlineStarBorder /> },
    { key: "hired",        label: "Hired",           value: stats.hired,        icon: <MdOutlineCheckCircle /> },
    { key: "rejected",     label: "Rejected",        value: stats.rejected,     icon: <MdOutlineCancel /> },
  ];

  const rangeText =
    range === "CUSTOM"
      ? (fromDate && toDate ? `${prettyDate(fromDate)} – ${prettyDate(toDate)}` : "Pick a date range")
      : `${prettyDate(fromDate)} – ${prettyDate(toDate)}`;

  return (
    <div className="ho-section">
      <div className="ho-header">
        <div>
          <p className="ho-title">Hiring Overview</p>
          <span className="ho-range-text">
            <MdOutlineCalendarToday className="ho-range-icon" /> {rangeText}
          </span>
        </div>

        <div className="ho-range-controls">
          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                width="170px"
                value={range}
                onChange={(e) => {
                  setRange(e.target.value);
                  setCustomFrom("");
                  setCustomTo("");
                }}
              >
                {RANGE_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </StyledSelect>
              <StyledLabel hasValue className="user-label">Date Range</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          {range === "CUSTOM" && (
            <>
              <StyledInputGroup>
                <StyledInputContainer>
                  <StyledInput
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    style={{ width: "150px" }}
                    max="9999-12-31"
                  />
                  <StyledLabel hasValue className="user-label">From</StyledLabel>
                </StyledInputContainer>
              </StyledInputGroup>
              <StyledInputGroup>
                <StyledInputContainer>
                  <StyledInput
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    style={{ width: "150px" }}
                    max="9999-12-31"
                  />
                  <StyledLabel hasValue className="user-label">To</StyledLabel>
                </StyledInputContainer>
              </StyledInputGroup>
            </>
          )}
        </div>
      </div>

      <div className="ho-grid">
        {cards.map((card) => (
          <div key={card.key} className="ho-card">
            <div className="ho-icon">{card.icon}</div>
            <div className="ho-card-body">
              <span className={loading ? "ho-value ho-value-loading" : "ho-value"}>
                {loading ? "—" : card.value.toLocaleString()}
              </span>
              <span className="ho-label">{card.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HiringOverview;
